import React from 'react';
import { useNavigate } from 'react-router-dom';
import CottageIcon from '../components/CottageIcon';
import Footer from '../components/Footer';
import SEO from '../components/SEO';

const PrivacyPolicy = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen" style={{ backgroundColor: 'var(--color-bg)' }}>
      <SEO title="Privacy Policy" description="Read the Cahoots privacy policy to understand how we collect, use, and protect your data." />
      {/* Header */}
      <div style={{ background: 'linear-gradient(to bottom, var(--color-bg), var(--color-surface))' }}>
        <div className="max-w-4xl mx-auto px-4 py-16 text-center">
          <CottageIcon size="text-4xl" className="mb-4" />
          <h1 className="heading-1 gradient-text mb-4">Privacy Policy</h1>
          <p className="text-lg" style={{ color: 'var(--color-text-muted)' }}>
            Last Updated: March 30, 2026
          </p>
        </div>
      </div>

      {/* Content */}
      <div className="py-8" style={{ backgroundColor: 'var(--color-surface)' }}>
        <div className="max-w-4xl mx-auto px-4">
          <div className="card p-8" style={{ backgroundColor: 'var(--color-bg)' }}>
            
            <section className="mb-8">
              <h2 className="text-2xl font-bold mb-4" style={{ color: 'var(--color-text)' }}>Introduction</h2>
              <p className="mb-4" style={{ color: 'var(--color-text)' }}>
                Cahoots ("we," "our," or "us") is committed to protecting your privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our AI-powered software planning application and services.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold mb-4" style={{ color: 'var(--color-text)' }}>Information We Collect</h2>
              
              <h3 className="text-xl font-semibold mb-3" style={{ color: 'var(--color-text)' }}>1. Account Information</h3>
              <ul className="list-disc list-inside mb-4 space-y-2" style={{ color: 'var(--color-text)' }}>
                <li><strong>Authentication Data</strong>: When you sign in using OAuth providers (Google, Apple), we receive basic profile information including your email address and name</li>
                <li><strong>User Identification</strong>: We generate and store unique user identifiers to isolate your tasks from other users</li>
              </ul>

              <h3 className="text-xl font-semibold mb-3" style={{ color: 'var(--color-text)' }}>2. Planning Artifacts</h3>
              <ul className="list-disc list-inside mb-4 space-y-2" style={{ color: 'var(--color-text)' }}>
                <li><strong>Ideas and Plans</strong>: We collect and store the product ideas, specifications, plans, features, user stories, and event models you create within the application</li>
                <li><strong>Artifact Metadata</strong>: We collect information about artifact status, relationships, and version history</li>
              </ul>

              <h3 className="text-xl font-semibold mb-3" style={{ color: 'var(--color-text)' }}>3. AI Interaction Data</h3>
              <ul className="list-disc list-inside mb-4 space-y-2" style={{ color: 'var(--color-text)' }}>
                <li><strong>Usage Patterns</strong>: We collect anonymized data about how you interact with AI-generated outputs to improve service quality</li>
                <li><strong>Performance Metrics</strong>: We collect system performance data to monitor and improve our AI planning pipelines</li>
              </ul>

              <h3 className="text-xl font-semibold mb-3" style={{ color: 'var(--color-text)' }}>4. Context and External Data</h3>
              <ul className="list-disc list-inside mb-4 space-y-2" style={{ color: 'var(--color-text)' }}>
                <li><strong>Internet Context Fetching</strong>: Our system may retrieve publicly available information from the internet to provide relevant context for your tasks and projects</li>
                <li><strong>External References</strong>: We may access and cache publicly available documentation, best practices, and technical resources related to your project requirements</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold mb-4" style={{ color: 'var(--color-text)' }}>How We Use Your Information</h2>
              
              <h3 className="text-xl font-semibold mb-3" style={{ color: 'var(--color-text)' }}>Primary Uses</h3>
              <ul className="list-disc list-inside mb-4 space-y-2" style={{ color: 'var(--color-text)' }}>
                <li><strong>Service Delivery</strong>: To provide, maintain, and improve our project management services</li>
                <li><strong>Planning Generation</strong>: To generate product specifications, features, user stories, event models, and requirements using our AI-powered pipeline</li>
                <li><strong>User Experience</strong>: To personalize and optimize your experience with our application</li>
              </ul>

              <h3 className="text-xl font-semibold mb-3" style={{ color: 'var(--color-text)' }}>AI Service Improvement</h3>
              <ul className="list-disc list-inside mb-4 space-y-2" style={{ color: 'var(--color-text)' }}>
                <li><strong>Quality Improvement</strong>: We use aggregated, anonymized data to improve the quality of AI-generated planning artifacts</li>
                <li><strong>Algorithm Enhancement</strong>: We use performance data to optimize our AI planning pipelines and prompt engineering</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold mb-4" style={{ color: 'var(--color-text)' }}>Data Security</h2>
              <ul className="list-disc list-inside mb-4 space-y-2" style={{ color: 'var(--color-text)' }}>
                <li><strong>Encryption</strong>: All data is encrypted in transit and at rest</li>
                <li><strong>Access Controls</strong>: We implement strict access controls to limit who can access your data</li>
                <li><strong>Regular Audits</strong>: We conduct regular security audits and assessments</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold mb-4" style={{ color: 'var(--color-text)' }}>We Do Not Sell Your Data</h2>
              <p className="mb-4" style={{ color: 'var(--color-text)' }}>
                We do not sell, trade, or otherwise transfer your personal information to third parties for commercial purposes.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold mb-4" style={{ color: 'var(--color-text)' }}>Your Rights and Choices</h2>
              <ul className="list-disc list-inside mb-4 space-y-2" style={{ color: 'var(--color-text)' }}>
                <li><strong>Access</strong>: You can access and review your account information and task data</li>
                <li><strong>Correction</strong>: You can update or correct your account information</li>
                <li><strong>Deletion</strong>: You can request deletion of your account and associated data</li>
                <li><strong>Export</strong>: You can export your ideas, plans, and associated artifacts</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold mb-4" style={{ color: 'var(--color-text)' }}>Third-Party Services</h2>
              <p className="mb-4" style={{ color: 'var(--color-text)' }}>
                We integrate with third-party OAuth providers (Google, Apple) for authentication. Their privacy policies govern the data they collect. When fetching context from the internet, we may access publicly available information from various sources.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold mb-4" style={{ color: 'var(--color-text)' }}>Children's Privacy</h2>
              <p className="mb-4" style={{ color: 'var(--color-text)' }}>
                Our service is not intended for children under 13 years of age. We do not knowingly collect personal information from children under 13.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold mb-4" style={{ color: 'var(--color-text)' }}>Changes to This Privacy Policy</h2>
              <p className="mb-4" style={{ color: 'var(--color-text)' }}>
                We may update this Privacy Policy from time to time. We will notify you of any material changes by posting the updated policy on our website, sending email notifications for significant changes, and displaying prominent notices within our application.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold mb-4" style={{ color: 'var(--color-text)' }}>Contact Information</h2>
              <p className="mb-4" style={{ color: 'var(--color-text)' }}>
                If you have questions or concerns about this Privacy Policy or our data practices, please contact us at:
              </p>
              <div className="p-4 rounded-lg border" style={{ backgroundColor: 'var(--color-surface)', borderColor: 'var(--color-border)' }}>
                <p style={{ color: 'var(--color-text)' }}><strong>Email:</strong> privacy@cahoots.cc</p>
                <p style={{ color: 'var(--color-text)' }}><strong>Website:</strong> https://cahoots.cc</p>
              </div>
            </section>

            <div className="text-center pt-8">
              <button
                className="btn btn-primary"
                onClick={() => navigate(-1)}
              >
                Go Back
              </button>
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default PrivacyPolicy;